import { useState, useRef, useEffect } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate, useSearchParams } from "react-router-dom"
import {
  ChevronDown, ChevronUp, Search, Video,
  Clock, Users, Calendar, Mic, Filter, BarChart2
} from "lucide-react"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/clientWeb/components/ui/table"

interface Meeting {
  id: string
  title: string
  host: string
  participants: number
  startTime: string
  duration: string
  status: "completed" | "cancelled" | "live"
  hasRecording: boolean
  sessionId: string
}

type SortKey = "title" | "host" | "participants" | "startTime" | "duration"
type SortDir = "asc" | "desc"

function FilterDropdown({ value, onChange, filters }: { value: string; onChange: (v: string) => void; filters: string[] }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false) }
    document.addEventListener("mousedown", h)
    return () => document.removeEventListener("mousedown", h)
  }, [])
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-2 border border-stroke-100 dark:border-border dark:border-border bg-card dark:bg-card rounded-lg px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 hover:border-primary transition-colors">
        <Filter className="w-3.5 h-3.5" /> {value} <ChevronDown className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-10 z-20 bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-xl shadow-lg min-w-[140px] py-1">
          {filters.map(f => (
            <button key={f} onClick={() => { onChange(f); setOpen(false) }}
              className={`block w-full text-left px-4 py-2 text-sm transition-colors ${value === f ? "text-primary font-semibold" : "text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"}`}>
              {f}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function SortIcon({ col, sortKey, sortDir }: { col: SortKey; sortKey: SortKey; sortDir: SortDir }) {
  if (col !== sortKey) return <ChevronUp className="w-3 h-3 opacity-20" />
  return sortDir === "asc" ? <ChevronUp className="w-3 h-3 text-primary" /> : <ChevronDown className="w-3 h-3 text-primary" />
}

export default function MeetingsList() {
  const t = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const type = searchParams.get("type") || "total"

  const FILTERS = [t("analytics.filterToday"), t("analytics.filterYesterday"), t("analytics.filterLastWeek"), t("analytics.filterLastMonth")]

  const [filter, setFilter] = useState(t("analytics.filterToday"))
  const [search, setSearch] = useState("")
  const [sortKey, setSortKey] = useState<SortKey>("startTime")
  const [sortDir, setSortDir] = useState<SortDir>("desc")

  // No analytics API yet (see services/analytics.repo.ts), so there are no
  // meetings to list. These used to be invented per-filter lists ("Morning
  // Standup" hosted by "Sarah Wilson"…) behind a timed fake loading spinner.
  const meetings: Meeting[] = []

  const isRecordings = type === "recordings"
  const rawData = isRecordings ? meetings.filter(m => m.hasRecording) : meetings

  const filtered = rawData
    .filter(m =>
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.host.toLowerCase().includes(search.toLowerCase())
    )
    .sort((a, b) => {
      let av: any = a[sortKey], bv: any = b[sortKey]
      if (sortKey === "participants" || sortKey === "duration") {
        av = parseFloat(String(av)); bv = parseFloat(String(bv))
      }
      if (av < bv) return sortDir === "asc" ? -1 : 1
      if (av > bv) return sortDir === "asc" ? 1 : -1
      return 0
    })

  const handleSort = (key: SortKey) => {
    if (key === sortKey) setSortDir(d => d === "asc" ? "desc" : "asc")
    else { setSortKey(key); setSortDir("asc") }
  }

  const title = isRecordings ? t("analytics.totalRecordings") : type === "live" ? t("analytics.liveMeetings") : t("analytics.totalMeetings")

  const cols: { key: SortKey; label: string }[] = [
    { key: "title", label: t("analytics.title") },
    { key: "host", label: t("analytics.host") },
    { key: "participants", label: t("common.participants") },
    { key: "startTime", label: t("analytics.startTime") },
    { key: "duration", label: t("analytics.duration") },
  ]

  return (
    <div className="flex flex-col h-full w-full overflow-y-auto bg-background p-4 md:p-6 gap-5">
      {/* Breadcrumb + Header */}
      <div>
        <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
          <button onClick={() => navigate("/analytics")} className="hover:text-primary font-semibold text-primary transition-colors flex items-center gap-1">
            <BarChart2 className="w-3.5 h-3.5 text-primary" /> {t("analytics.title")}
          </button>
          <span>/</span>
          <span className="text-slate-700 dark:text-slate-200 font-medium">{title}</span>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            {/* <button onClick={() => navigate("/analytics")}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </button> */}
            <div>
              <h1 className="text-xl font-bold text-slate-900 dark:text-foreground/80 dark:text-white">{title}</h1>
              <p className="text-xs text-slate-400 mt-0.5">{filtered.length} {t("analytics.meetingsFound")}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder={t("analytics.searchMeetingsPlaceholder")}
                className="pl-8 pr-3 py-2 text-sm bg-card dark:bg-card border border-stroke-100 dark:border-border dark:border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary text-slate-900 dark:text-foreground/80 dark:text-white w-[180px]"
              />
            </div>
            <FilterDropdown value={filter} onChange={setFilter} filters={FILTERS} />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card dark:bg-card rounded-2xl border border-stroke-100 dark:border-border dark:border-border shadow-xs overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Video className="w-16 h-16 text-slate-200 dark:text-slate-700" />
            <p className="text-sm font-semibold text-slate-600 dark:text-slate-400">{t("analytics.noMeetingsFound")}</p>
            {search && <p className="text-xs text-slate-400">{t("analytics.tryAdjustingFilter")}</p>}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-stroke-100 dark:border-border dark:border-border bg-slate-50 dark:bg-card/80">
                  {cols.map(c => (
                    <TableHead key={c.key}
                      className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider cursor-pointer hover:text-primary transition-colors select-none"
                      onClick={() => handleSort(c.key)}>
                      <span className="flex items-center gap-1">{c.label} <SortIcon col={c.key} sortKey={sortKey} sortDir={sortDir} /></span>
                    </TableHead>
                  ))}
                  <TableHead className="text-left px-4 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">{t("analytics.status")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(m => (
                  <TableRow key={m.id}
                    className="border-b border-slate-50 dark:border-border/40 hover:bg-slate-50 dark:hover:bg-slate-700/20 transition-colors cursor-pointer"
                    onClick={() => navigate(`/analytics/meetings/${m.sessionId}/${m.id}`)}>
                    <TableCell className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                          {isRecordings ? <Mic className="w-3.5 h-3.5 text-purple-500" /> : <Video className="w-3.5 h-3.5 text-primary" />}
                        </div>
                        <span className="text-sm font-semibold text-primary dark:text-slate-200 hover:text-primary">{m.title}</span>
                      </div>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">{m.host}</TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Users className="w-3.5 h-3.5" /> {m.participants}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Calendar className="w-3.5 h-3.5" /> {m.startTime}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-400">
                        <Clock className="w-3.5 h-3.5" /> {m.duration}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full ${
                        m.status === "completed" ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400"
                        : m.status === "live" ? "bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400 animate-pulse"
                        : "bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400"
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${m.status === "completed" ? "bg-emerald-500" : m.status === "live" ? "bg-red-500" : "bg-slate-400"}`} />
                        {m.status === "completed" ? t("analytics.completed") : m.status === "live" ? t("analytics.live") : t("analytics.cancelled")}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  )
}
